// src/Pages/Purchase/PurchaseData/AddBills.jsx
import React, { useState, useEffect } from "react";
import { useBills } from "../../../context/BillsContext";
import { useNavigate, Link } from "react-router-dom";
import "../../../Pages/Purchase/PurchaseData/AddBills.css";
import { API_URL } from "../../../config";

const AddBills = () => {
  const { addBill } = useBills();
  const navigate = useNavigate();

  const [billNo, setBillNo] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [showVendorWarning, setShowVendorWarning] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);

  const [formData, setFormData] = useState({
    referenceNo: "",
    date: new Date().toISOString().split("T")[0],
    dueDate: "",
    terms: "Due on Receipt",
    accountsPayable: "", // ← NEW FIELD ADDED
    subject: "",
    items: [
      {
        id: Date.now(),
        description: "",
        account: "",
        qty: 1,
        rate: 0,
        customerDetails: "",
        amount: 0,
      },
    ],
    discountType: "%",
    discountValue: 0,
    tdsTcs: false,
    tdsTcsPercent: 0,
    adjustment: 0,
    customerNotes: "",
  });

  // Fetch next Bill number
  useEffect(() => {
    const fetchNextBillNo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bills/next-bill-no`, {
          credentials: "include",
        });
        const data = await res.json();
        setBillNo(
          data.success && data.nextBillNo ? data.nextBillNo : "BILL-000001"
        );
      } catch (err) {
        console.error(err);
        setBillNo("BILL-000001");
      }
    };
    fetchNextBillNo();
  }, []);

  // Load Vendors
  useEffect(() => {
    const fetchVendors = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;
      try {
        setLoadingVendors(true);
        const res = await fetch(`${API_URL}/api/vendors/get?userId=${userId}`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) =>
            getVendorDisplayName(a).localeCompare(getVendorDisplayName(b))
          );
          setVendors(sorted);
        }
      } catch (err) {
        console.error("Failed to load vendors:", err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  const getVendorDisplayName = (vend) => {
    const name = [vend.firstName, vend.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      name || vend.displayName || vend.companyName || "Unnamed Vendor";
    const secondary =
      vend.companyName && vend.companyName !== primary
        ? ` (${vend.companyName})`
        : "";
    return primary + secondary;
  };

  // Item handlers
  const updateItem = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "qty" || field === "rate") {
            updated.amount =
              Number(updated.qty || 0) * Number(updated.rate || 0);
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeItem = (id) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          description: "",
          account: "",
          qty: 1,
          rate: 0,
          customerDetails: "",
          amount: 0,
        },
      ],
    }));
  };

  // Calculations
  const subtotal = formData.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    formData.discountType === "%"
      ? (subtotal * formData.discountValue) / 100
      : formData.discountValue;
  const tdsTcsAmount = formData.tdsTcs
    ? (subtotal * formData.tdsTcsPercent) / 100
    : 0;
  const total =
    subtotal - discountAmount - tdsTcsAmount + Number(formData.adjustment || 0);

  // Save Bill
  const handleSave = async (status = "DRAFT") => {
    if (!selectedVendor) {
      setShowVendorWarning(true);
      return;
    }
    const userId = localStorage.getItem("userId");
    if (!userId) return alert("Session expired");

    const billData = {
      userId,
      billNo,
      referenceNo: formData.referenceNo,
      vendorName: selectedVendor,
      date: formData.date,
      dueDate: formData.dueDate || formData.date,
      terms: formData.terms,
      accountsPayable: formData.accountsPayable, // ← SAVED TO DB
      subject: formData.subject,
      status,
      items: formData.items.map((i) => ({
        description: i.description,
        account: i.account,
        qty: Number(i.qty),
        rate: Number(i.rate),
        customerDetails: i.customerDetails,
        amount: Number(i.amount),
      })),
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      tdsTcs: formData.tdsTcs,
      tdsTcsPercent: formData.tdsTcs ? formData.tdsTcsPercent : 0,
      adjustment: Number(formData.adjustment || 0),
      customerNotes: formData.customerNotes,
      subtotal,
      discountAmount,
      tdsTcsAmount,
      total,
      amount: total,
      balanceDue: total,
    };

    try {
      const res = await fetch(`${API_URL}/api/bills/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(billData),
      });
      const result = await res.json();
      if (result.success) {
        addBill(result.data);
        alert(status === "OPEN" ? "Bill Saved & Opened!" : "Saved as Draft");
        navigate("/purchase/bills");
      } else {
        alert("Failed: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("Network error");
    }
  };

  return (
    <div className="add-bill-page">
      <div className="bill-header">
        <h1>New Bill</h1>
      </div>

      <div className="bill-form">
        {/* Vendor & Bill# */}
        <div className="form-row top-row">
          <div className="form-group vendor-group">
            <label>
              Vendor Name <span className="required">*</span>
            </label>
            <select
              value={selectedVendor}
              onChange={(e) => {
                setSelectedVendor(e.target.value);
                setShowVendorWarning(false);
              }}
              disabled={loadingVendors}
            >
              <option value="">
                {loadingVendors ? "Loading..." : "Select Vendor"}
              </option>
              {vendors.map((v) => (
                <option key={v._id} value={getVendorDisplayName(v)}>
                  {getVendorDisplayName(v)}
                </option>
              ))}
            </select>
            <div className="add-vendor-link">
              <Link to="/purchase/vendors/new">+ Add New Vendor</Link>
            </div>
            {showVendorWarning && (
              <small className="warning">Please select a vendor</small>
            )}
          </div>
          <div className="form-group">
            <label>Bill#</label>
            <input type="text" value={billNo} readOnly />
          </div>
        </div>

        {/* One field per row */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>Order Number</label>
            <input
              type="text"
              value={formData.referenceNo}
              onChange={(e) =>
                setFormData({ ...formData, referenceNo: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Bill Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) =>
                setFormData({ ...formData, dueDate: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Payment Terms</label>
            <select
              value={formData.terms}
              onChange={(e) =>
                setFormData({ ...formData, terms: e.target.value })
              }
            >
              <option>Due on Receipt</option>
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Net 60</option>
              <option>Due end of the month</option>
              <option>Due end of next month</option>
            </select>
          </div>
        </div>

        {/* ← NEW FIELD: Accounts Payable */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>Accounts Payable</label>
            <input
              type="text"
              value={formData.accountsPayable}
              onChange={(e) =>
                setFormData({ ...formData, accountsPayable: e.target.value })
              }
              placeholder="e.g. Accounts Payable, Creditors, Trade Payables"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Subject</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
            />
          </div>
        </div>

        {/* Rest of the form remains exactly the same */}
        {/* Item Table */}
        <div className="item-section">
          <h3>Item Table</h3>
          <table className="item-table">
            <thead>
              <tr>
                <th>ITEM DETAILS</th>
                <th>ACCOUNT</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>CUSTOMER DETAILS</th>
                <th>AMOUNT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(item.id, "description", e.target.value)
                      }
                      placeholder="Item description"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.account}
                      onChange={(e) =>
                        updateItem(item.id, "account", e.target.value)
                      }
                      placeholder="Account"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(item.id, "qty", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(item.id, "rate", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.customerDetails}
                      onChange={(e) =>
                        updateItem(item.id, "customerDetails", e.target.value)
                      }
                      placeholder="Customer details"
                    />
                  </td>
                  <td className="amount">₹{item.amount.toFixed(2)}</td>
                  <td>
                    {formData.items.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItemRow} className="add-row-btn">
            + Add New Row
          </button>
        </div>

        {/* Calculations */}
        <div className="calculations">
          <div className="calc-left">
            <div>Sub Total</div>
            <div className="calc-row">
              Discount
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({ ...formData, discountType: e.target.value })
                }
              >
                <option value="%">%</option>
                <option value="₹">₹</option>
              </select>
              <input
                type="number"
                min="0"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: Number(e.target.value) || 0,
                  })
                }
                style={{ width: 80 }}
              />
            </div>

            <div className="calc-row">
              <label>
                <input
                  type="checkbox"
                  checked={formData.tdsTcs}
                  onChange={(e) =>
                    setFormData({ ...formData, tdsTcs: e.target.checked })
                  }
                />
                TDS/TCS
              </label>
              {formData.tdsTcs && (
                <>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.tdsTcsPercent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tdsTcsPercent: Number(e.target.value) || 0,
                      })
                    }
                    style={{ width: 70 }}
                  />
                  <span>%</span>
                </>
              )}
            </div>

            <div className="calc-row">
              Adjustment
              <input
                type="number"
                value={formData.adjustment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adjustment: Number(e.target.value) || 0,
                  })
                }
                style={{ width: 100 }}
              />
            </div>

            <div className="total">Total (₹)</div>
          </div>

          <div className="calc-right">
            <div>₹{subtotal.toFixed(2)}</div>
            <div>-₹{discountAmount.toFixed(2)}</div>
            {formData.tdsTcs && <div>-₹{tdsTcsAmount.toFixed(2)}</div>}
            <div>₹{(formData.adjustment || 0).toFixed(2)}</div>
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>

        {/* Notes & Files */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>Notes</label>
            <textarea
              rows="4"
              value={formData.customerNotes}
              onChange={(e) =>
                setFormData({ ...formData, customerNotes: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label>Attach Files</label>
            <input type="file" multiple />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-bar">
          <div className="left-actions">
            <button onClick={() => handleSave("DRAFT")} className="draft-btn">
              Save as Draft
            </button>
            <button onClick={() => handleSave("OPEN")} className="open-btn">
              Save as Open
            </button>
            <button
              onClick={() => navigate("/purchase/bills")}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBills;
